import {
  AdminPricingApiService,
  CABINET_PATHS,
  type AdminDomainPricingVersionSummary,
  type PricingDomain,
} from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import { bindStaffPageTitleCount } from './staff-page-title-count.util';
import {
  formatPricingDate,
  formatPricingRate,
  isPricingVersionCurrent,
  isPricingVersionFuture,
} from './staff-pricing.util';

@Component({
  selector: 'lib-staff-pricing-domain-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, CabinetButtonComponent, RouterLink],
  templateUrl: './staff-pricing-domain-list.component.html',
  styleUrl: './staff-pricing-domain-list.component.scss',
})
export class StaffPricingDomainListComponent implements OnInit {
  readonly domain = input.required<PricingDomain>();
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #pricingApi = inject(AdminPricingApiService);
  readonly #router = inject(Router);

  protected readonly staffCabinetPath = this.#cabinetPaths.staff;
  protected readonly loading = signal(true);
  protected readonly initialized = signal(false);
  protected readonly loadError = signal(false);

  private readonly refreshSubject = new Subject<void>();

  protected readonly listResponse = toSignal(
    this.refreshSubject.pipe(
      tap(() => {
        this.loading.set(true);
        this.loadError.set(false);
      }),
      switchMap(() =>
        this.#pricingApi.listVersions(this.domain()).pipe(
          tap(() => {
            this.loading.set(false);
            this.initialized.set(true);
            this.loadError.set(false);
          }),
          catchError(() => {
            this.loading.set(false);
            this.initialized.set(true);
            this.loadError.set(true);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  protected readonly versions = computed(
    () => this.listResponse()?.versions ?? [],
  );

  protected readonly displayCount = computed(() => {
    if (!this.initialized()) {
      return null;
    }
    return this.versions().length;
  });

  protected readonly timelineSegments = computed(() => {
    const items = this.versions();
    if (items.length === 0) {
      return [];
    }
    const sorted = [...items].sort(
      (a, b) =>
        new Date(a.effective_from).getTime() -
        new Date(b.effective_from).getTime(),
    );
    return sorted.map((version, index) => ({
      version,
      isLast: index === sorted.length - 1,
    }));
  });

  constructor() {
    bindStaffPageTitleCount(this.embeddedInCabinetPage, this.displayCount);
  }

  public ngOnInit(): void {
    this.refreshSubject.next();
  }

  protected onCreate(): void {
    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'pricing',
      this.domain(),
      'create',
    ]);
  }

  protected versionLink(id: string): string {
    return `${this.staffCabinetPath}/pricing/${this.domain()}/${id}`;
  }

  protected formatDate = formatPricingDate;
  protected formatRate = formatPricingRate;
  protected isCurrent = isPricingVersionCurrent;
  protected isFuture = isPricingVersionFuture;

  protected versionSummary(version: AdminDomainPricingVersionSummary): string {
    if (this.domain() === 'storage') {
      return `${this.formatRate(version.base_rate_bynd_per_m3_day)} BYN/м³·сут`;
    }
    if (this.domain() === 'promocodes') {
      return String(version.promocode_count ?? 0);
    }
    return '';
  }
}
